import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const usd = (v, dp = 6) =>
  v == null ? "—" : `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: dp })}`;
const secs = (v) => (v == null ? "—" : `${Number(v).toLocaleString("en-US", { maximumFractionDigits: 2 })}s`);
const pct = (v) => (v == null ? "—" : `${Number(v).toFixed(2)}%`);

const CATEGORY_LABEL = {
  main_chunk: "Normal chunk window",
  main_tail_chunk: "Final partial chunk",
  repeat_full_window: "Repeated full window (retry / repeat pass)",
  gap_recovery_candidate: "Gap recovery (candidate)",
  hole_retranscription_candidate: "Hole re-transcription (candidate)",
  failed_request: "Failed request",
  duration_not_reported: "Duration not reported",
};
const CATEGORY_TONE = {
  main_chunk: "text-zinc-400",
  main_tail_chunk: "text-zinc-400",
  repeat_full_window: "text-amber-300",
  gap_recovery_candidate: "text-blue-300",
  hole_retranscription_candidate: "text-violet-300",
  failed_request: "text-red-300",
  duration_not_reported: "text-zinc-600",
};

export default function WhisperRequestAudit({ jobId, onClose }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: d } = await api.get(`/admin/spend/whisper-audit/requests?job_id=${jobId}`);
        if (alive) setData(d);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Could not load the per-request audit");
        onClose();
      }
    })();
    return () => {
      alive = false;
    };
  }, [jobId, onClose]);

  const t = data?.totals || {};
  const plan = data?.plan || {};

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-6xl bg-zinc-950 border-zinc-800 text-white max-h-[88vh] overflow-y-auto"
        data-testid="whisper-request-audit-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold tracking-tight">
            Whisper request audit · <span className="font-mono text-sm text-zinc-400">{jobId}</span>
          </DialogTitle>
        </DialogHeader>

        {!data ? (
          <div className="py-16 grid place-items-center">
            <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />
          </div>
        ) : data.message ? (
          <div className="border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-200">
            {data.message}
          </div>
        ) : (
          <div className="space-y-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                ["Submitted / duration", `${t.submitted_to_duration_ratio}×`, "ratio-tile"],
                ["Intentional chunk overlap", `${secs(t.intentional_chunk_overlap_seconds)} · ${pct(t.intentional_chunk_overlap_pct_of_submitted)}`, "intentional-tile"],
                ["Recovery / retry", `${secs(t.recovery_retry_seconds)} · ${pct(t.recovery_retry_pct_of_submitted)}`, "recovery-tile"],
                ["Repeated-audio cost", usd(t.total_repeated_cost_usd, 4), "repeat-cost-tile"],
              ].map(([label, value, tid]) => (
                <div key={label} className="border border-zinc-800 bg-zinc-900/40 p-3" data-testid={`wra-${tid}`}>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
                  <div className="text-base font-mono text-white mt-1">{value}</div>
                </div>
              ))}
            </div>

            <div className="text-[11px] font-mono text-zinc-500 space-y-1">
              <div>
                Video {secs(t.media_seconds)} · submitted {secs(t.submitted_seconds)} · chunk plan{" "}
                {plan.windows} windows ({secs(plan.main_pass_seconds)} main pass) · matched{" "}
                {plan.matched_requests} requests, {plan.unmatched_requests} additional ·
                reconciliation gap {secs(t.unexplained_seconds)}
              </div>
              <div>
                Intentional overlap {usd(t.intentional_overlap_cost_usd, 4)} · recovery/retry{" "}
                {usd(t.recovery_retry_cost_usd, 4)} · total Whisper {usd(t.whisper_cost_usd, 4)}
              </div>
            </div>

            <div className="border border-zinc-800" data-testid="wra-category-table">
              <div className="bg-zinc-900/60 px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-zinc-400">
                By category (inferred from submitted duration + chunk config)
              </div>
              <table className="w-full text-xs">
                <tbody>
                  {Object.entries(data.by_category || {}).map(([cat, b]) => (
                    <tr key={cat} className="border-t border-zinc-800/60" data-testid={`wra-category-${cat}`}>
                      <td className={`px-3 py-2 font-mono ${CATEGORY_TONE[cat] || "text-zinc-400"}`}>
                        {CATEGORY_LABEL[cat] || cat}
                      </td>
                      <td className="px-3 py-2 text-right text-zinc-400">{b.requests} req</td>
                      <td className="px-3 py-2 text-right text-zinc-400">{secs(b.submitted_seconds)} sent</td>
                      <td className="px-3 py-2 text-right text-amber-300">{secs(b.repeated_seconds)} repeated</td>
                      <td className="px-3 py-2 text-right font-mono text-white">{usd(b.cost_usd, 4)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="border border-zinc-800">
              <div className="bg-zinc-900/60 px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-zinc-400">
                Per request ({(data.rows || []).length})
              </div>
              <div className="max-h-80 overflow-y-auto">
                <table className="w-full text-[11px]" data-testid="wra-request-table">
                  <thead className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 sticky top-0 bg-zinc-950">
                    <tr>
                      <th className="text-left px-3 py-2">#</th>
                      <th className="text-left px-3 py-2">Time</th>
                      <th className="text-right px-3 py-2">Window</th>
                      <th className="text-right px-3 py-2">Submitted</th>
                      <th className="text-right px-3 py-2">Overlap w/ prev</th>
                      <th className="text-right px-3 py-2">Repeated</th>
                      <th className="text-left px-3 py-2">Category</th>
                      <th className="text-right px-3 py-2">Latency</th>
                      <th className="text-right px-3 py-2">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.rows || []).map((r) => (
                      <tr key={r.sequence} className="border-t border-zinc-800/60">
                        <td className="px-3 py-1.5 text-zinc-600 font-mono">{r.sequence}</td>
                        <td className="px-3 py-1.5 text-zinc-500 font-mono">
                          {new Date(r.created_at).toLocaleTimeString()}
                        </td>
                        <td className="px-3 py-1.5 text-right font-mono text-zinc-400">
                          {r.window_start == null
                            ? "not recorded"
                            : `${r.window_start}–${r.window_end}s`}
                        </td>
                        <td className="px-3 py-1.5 text-right font-mono text-zinc-300">
                          {secs(r.submitted_seconds)}
                        </td>
                        <td className="px-3 py-1.5 text-right font-mono text-zinc-500">
                          {r.overlap_with_previous_seconds == null ? "—" : secs(r.overlap_with_previous_seconds)}
                        </td>
                        <td className="px-3 py-1.5 text-right font-mono text-amber-300">
                          {secs(r.repeated_seconds)}
                        </td>
                        <td className={`px-3 py-1.5 font-mono ${CATEGORY_TONE[r.category] || "text-zinc-400"}`}>
                          {CATEGORY_LABEL[r.category] || r.category}
                        </td>
                        <td className="px-3 py-1.5 text-right text-zinc-600 font-mono">{r.latency_ms}ms</td>
                        <td className="px-3 py-1.5 text-right font-mono text-zinc-400">{usd(r.cost_usd, 5)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <ul className="text-[10px] font-mono text-zinc-600 space-y-1" data-testid="wra-limits">
              {(data.telemetry_limits || []).map((l) => (
                <li key={l.slice(0, 24)}>· {l}</li>
              ))}
            </ul>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
